import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Edit2, Filter, FolderTree, Plus, Search, Tag, Trash2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/common/FormSection';
import { amountToneClass, FinanceStatusBadge, toneForTransactionType } from '@/components/finance-v2/FinanceBadges';
import type { Category, CategoryType } from '../types/financeV2.types';
import { financeV2Service, type CreateCategoryDto, type UpdateCategoryDto, type CategoryFilters } from '../services/financeV2Service';
import { getErrorMessage } from '../utils/error.utils'

const TYPE_LABELS: Record<CategoryType, string> = {
  INCOME: 'Receita',
  EXPENSE: 'Despesa',
  TRANSFER: 'Transferência',
};

const DEFAULT_COLOR = '#3B82F6';

function ErrorBox({ message }: { message: string }) {
  return (
    <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{message}</p>
  );
}

export function FinanceV2CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CategoryFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await financeV2Service.categories.list({ ...filters, page, limit: 20 });
      setCategories(response.data.categories);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar categorias'));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setShowEditModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      return;
    }

    try {
      await financeV2Service.categories.delete(id);
      loadCategories();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao excluir categoria'));
    }
  };

  const handleSearch = (searchTerm: string) => {
    setFilters({ ...filters, search: searchTerm });
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const activeFilterCount = [filters.type, filters.isActive].filter((v) => v !== undefined).length;

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <FolderTree className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Categorias</h1>
          </div>
          <p className="text-sm text-muted-foreground">Gerencie as categorias de receitas e despesas</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="size-4" /> Nova Categoria
        </Button>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1 basis-56">
            <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              aria-label="Buscar categorias"
              placeholder="Buscar categorias..."
              className="pl-8"
              value={filters.search || ''}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          <Button
            variant="outline"
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
            aria-controls="category-filters-panel"
          >
            <Filter className="size-4" />
            Filtros
            {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
          </Button>
          {Object.keys(filters).length > 0 && (
            <Button variant="ghost" type="button" onClick={clearFilters}>Limpar</Button>
          )}
        </div>

        {showFilters && (
          <Card size="sm" id="category-filters-panel">
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
              <FormField label="Tipo" htmlFor="category-filter-type">
                <NativeSelect
                  id="category-filter-type"
                  value={filters.type || ''}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value as CategoryType || undefined })}
                >
                  <option value="">Todos</option>
                  <option value="INCOME">Receita</option>
                  <option value="EXPENSE">Despesa</option>
                  <option value="TRANSFER">Transferência</option>
                </NativeSelect>
              </FormField>
              <FormField label="Status" htmlFor="category-filter-status">
                <NativeSelect
                  id="category-filter-status"
                  value={filters.isActive !== undefined ? filters.isActive.toString() : ''}
                  onChange={(e) => setFilters({ ...filters, isActive: e.target.value === '' ? undefined : e.target.value === 'true' })}
                >
                  <option value="">Todos</option>
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </NativeSelect>
              </FormField>
            </CardContent>
          </Card>
        )}
      </div>

      {error && <ErrorBox message={error} />}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          <span className="sr-only">Carregando categorias...</span>
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-36" />)}
        </div>
      ) : categories.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-6 text-center">
            <FolderTree className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-base font-medium">Nenhuma categoria encontrada</h3>
            <p className="text-sm text-muted-foreground">Comece criando uma nova categoria</p>
            <Button className="mt-2" onClick={() => setShowCreateModal(true)}>
              <Plus className="size-4" /> Nova Categoria
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              onEdit={() => handleEdit(category)}
              onDelete={() => handleDelete(category.id)}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">Página {page} de {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
              <ChevronLeft className="size-4" /> Anterior
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
              Próxima <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}

      {showCreateModal && (
        <CreateCategoryModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadCategories();
          }}
        />
      )}

      {showEditModal && selectedCategory && (
        <EditCategoryModal
          category={selectedCategory}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCategory(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedCategory(null);
            loadCategories();
          }}
        />
      )}
    </div>
  );
}

function CategoryCard({ category, onEdit, onDelete }: { category: Category; onEdit: () => void; onDelete: () => void }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <Tag className="size-5" style={{ color: category.color || 'var(--primary)' }} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h3 className="break-words font-medium">{category.name}</h3>
              <p className="text-sm text-muted-foreground">{category.code}</p>
            </div>
          </div>
          <div className="flex shrink-0 gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" aria-label="Editar" className="text-muted-foreground" onClick={onEdit}>
                  <Edit2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Editar</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" aria-label="Excluir" className="text-muted-foreground hover:text-destructive" onClick={onDelete}>
                  <Trash2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Excluir</TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-muted-foreground">Tipo:</span>
          <Badge variant="outline" className={amountToneClass(toneForTransactionType(category.type))}>
            {TYPE_LABELS[category.type] ?? category.type}
          </Badge>
        </div>

        {category.description && (
          <p className="line-clamp-2 break-words text-sm text-muted-foreground">{category.description}</p>
        )}

        <div className="border-t border-border pt-3">
          <FinanceStatusBadge status={category.isActive ? 'Ativo' : 'Inativo'} />
        </div>
      </CardContent>
    </Card>
  );
}

function ColorField({ id, value, onChange }: { id: string; value: string; onChange: (color: string) => void }) {
  return (
    <FormField label="Cor" htmlFor={id}>
      <div className="flex gap-2">
        <input
          type="color"
          aria-label="Escolher cor"
          className="h-8 w-16 shrink-0 cursor-pointer rounded-lg border border-input bg-transparent"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <Input id={id} type="text" className="min-w-0 flex-1" value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </FormField>
  );
}

function CreateCategoryModal({
  onClose,
  onSuccess
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState<CreateCategoryDto>({
    name: '',
    code: '',
    type: 'EXPENSE',
    color: DEFAULT_COLOR,
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await financeV2Service.categories.create(formData);
      onSuccess();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao criar categoria'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !loading) onClose() }}>
      <DialogContent fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Nova Categoria</DialogTitle>
        </DialogHeader>

        {error && <ErrorBox message={error} />}

        <form id="create-category-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
          <FormField label="Nome *" htmlFor="category-name">
            <Input
              id="category-name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </FormField>

          <FormField label="Código *" htmlFor="category-code">
            <Input
              id="category-code"
              type="text"
              required
              placeholder="Ex: HON-ADV"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            />
          </FormField>

          <FormField label="Tipo *" htmlFor="category-type">
            <NativeSelect
              id="category-type"
              required
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as CategoryType })}
            >
              <option value="INCOME">Receita</option>
              <option value="EXPENSE">Despesa</option>
              <option value="TRANSFER">Transferência</option>
            </NativeSelect>
          </FormField>

          <ColorField id="category-color" value={formData.color || DEFAULT_COLOR} onChange={(color) => setFormData({ ...formData, color })} />

          <FormField label="Descrição" htmlFor="category-description">
            <Textarea
              id="category-description"
              rows={3}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </FormField>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button type="submit" form="create-category-form" disabled={loading}>
            {loading ? 'Criando...' : 'Criar Categoria'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditCategoryModal({
  category,
  onClose,
  onSuccess
}: {
  category: Category;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState<UpdateCategoryDto>({
    name: category.name,
    description: category.description || '',
    color: category.color || DEFAULT_COLOR,
    isActive: category.isActive
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await financeV2Service.categories.update(category.id, formData);
      onSuccess();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao atualizar categoria'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !loading) onClose() }}>
      <DialogContent fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Editar Categoria</DialogTitle>
        </DialogHeader>

        {error && <ErrorBox message={error} />}

        <form id="edit-category-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
          <FormField label="Nome *" htmlFor="edit-category-name">
            <Input
              id="edit-category-name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </FormField>

          <FormField label="Código" htmlFor="edit-category-code" hint="O código não pode ser alterado">
            <Input id="edit-category-code" type="text" disabled value={category.code} />
          </FormField>

          <FormField label="Tipo" htmlFor="edit-category-type" hint="O tipo não pode ser alterado">
            <Input id="edit-category-type" type="text" disabled value={TYPE_LABELS[category.type] ?? category.type} />
          </FormField>

          <ColorField id="edit-category-color" value={formData.color || DEFAULT_COLOR} onChange={(color) => setFormData({ ...formData, color })} />

          <FormField label="Descrição" htmlFor="edit-category-description">
            <Textarea
              id="edit-category-description"
              rows={3}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </FormField>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="size-4 rounded-sm border-input accent-primary"
            />
            <Label htmlFor="isActive" className="font-normal">Categoria ativa</Label>
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button type="submit" form="edit-category-form" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
